// Pure, side-effect-free view-model logic for the ops console.
// Kept free of React and network code so it is unit-testable (logic.test.ts).

import type { CSSProperties } from "react";

export type PlatformRole = "NONE" | "STAFF" | "SUPERADMIN";
export type GateState = "pass" | "fail" | "na";
export type PillTone = "ok" | "danger" | "warn" | "neutral" | "violet" | "sky";
export type AuditCategory =
  | "credit"
  | "kill"
  | "lookup"
  | "danger"
  | "role"
  | "org"
  | "ok"
  | "neutral";

export interface Gate {
  key: string;
  label: string;
  state: GateState;
  detail: string;
  reason: string;
}

export type CreditDirection = "add" | "subtract";

/**
 * The role the UI should gate on. View-as-staff can only DOWNGRADE a superadmin
 * to the staff view; it never elevates and is never an authorization mechanism
 * (the backend re-derives the real role for every request). FR-008.
 */
export function effectiveRole(realRole: PlatformRole, viewAsStaff: boolean): PlatformRole {
  if (realRole === "SUPERADMIN") return viewAsStaff ? "STAFF" : "SUPERADMIN";
  return realRole; // STAFF / NONE are never elevated by the toggle
}

/** Derive the Audit-page category (dot color) from the raw action string. */
export function auditCategory(action: string): AuditCategory {
  const a = (action || "").toLowerCase();
  if (a.startsWith("telephony.credit")) return "credit";
  if (a === "dialing.disable" || a === "dialing.enable") return "kill";
  if (a.startsWith("conversation.")) return "lookup";
  if (a === "user.unsuspend" || a === "user.restore") return "ok";
  if (a === "user.suspend" || a === "user.disable" || a === "user.membership_remove")
    return "danger";
  if (a === "user.role_change" || a.startsWith("platform_role")) return "role";
  if (a.startsWith("org")) return "org";
  return "neutral";
}

/** Humanized label for an audit entry (action + `after` payload). */
export function auditLabel(action: string, after: Record<string, unknown> | null): string {
  const d = after || {};
  const mins = (s: unknown) => secondsToMinutes(Number(s) || 0);
  switch (action) {
    case "telephony.credit_adjust":
      return `Credits ${d.direction === "add" ? "+" : "−"}${mins(d.seconds)} min`;
    case "telephony.credit_grant":
      return `Credits +${mins(d.seconds)} min (grant)`;
    case "dialing.disable":
      return "Dialing disabled";
    case "dialing.enable":
      return "Dialing enabled";
    case "user.role_change":
      return `Role → ${(d.system_role as string) ?? (d.role as string) ?? "?"}`;
    case "user.membership_add":
      return "Added to org";
    case "user.membership_remove":
      return "Removed from org";
    case "user.suspend":
      return "User suspended";
    case "user.unsuspend":
      return "User unsuspended";
    case "user.disable":
      return "User disabled";
    case "user.restore":
      return "User restored";
    case "platform_role.grant":
      return `Platform role → ${(d.platform_role as string) ?? "?"}`;
    case "org.create":
      return "Org created";
    case "org.update":
      return "Org updated";
    case "conversation.lookup":
      return "Call lookup";
    case "conversation.list":
      return "Call list · by org";
    default:
      return action;
  }
}

/** Overall dial verdict from a gate list — AND of applicable (non-na) gates. */
export function computeVerdict(gates: Gate[]): { canDial: boolean; reasons: string[] } {
  const applicable = gates.filter((g) => g.state !== "na");
  const canDial = applicable.length > 0 && applicable.every((g) => g.state === "pass");
  const reasons = gates
    .filter((g) => g.state === "fail")
    .map((g) => g.reason)
    .filter(Boolean);
  return { canDial, reasons };
}

export function gateTone(state: GateState): PillTone {
  return state === "pass" ? "ok" : state === "fail" ? "danger" : "neutral";
}

/** Advisory before→after preview for the credit modal. Server is authoritative. */
export function creditPreview(
  balanceSeconds: number,
  direction: CreditDirection,
  minutes: number,
): { afterMinutes: number; overdraw: boolean } {
  const delta = (Number(minutes) || 0) * 60;
  const afterSeconds = direction === "add" ? balanceSeconds + delta : balanceSeconds - delta;
  return { afterMinutes: Math.round(afterSeconds / 60), overdraw: afterSeconds < 0 };
}

export function minutesToSeconds(minutes: number): number {
  return Math.round(Number(minutes) || 0) * 60;
}
export function secondsToMinutes(seconds: number): number {
  return Math.round((Number(seconds) || 0) / 60);
}

// --- shared style helpers (mirror the mock's pill()/dotColor()) --------------

const PILL_COLORS: Record<PillTone, [string, string]> = {
  ok: ["var(--success-100)", "var(--success-500)"],
  danger: ["var(--danger-100)", "var(--danger-500)"],
  warn: ["#FBEEDD", "#8A5209"],
  neutral: ["var(--ink-100)", "var(--ink-700)"],
  violet: ["var(--violet-50)", "var(--violet-500)"],
  sky: ["var(--info-100)", "var(--info-500)"],
};

export function pillStyle(tone: PillTone): CSSProperties {
  const [bg, fg] = PILL_COLORS[tone] || PILL_COLORS.neutral;
  return {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    fontFamily: "var(--font-mono)",
    fontSize: 11,
    letterSpacing: "0.03em",
    textTransform: "uppercase",
    color: fg,
    background: bg,
    borderRadius: 999,
    padding: "3px 10px",
    whiteSpace: "nowrap",
    lineHeight: 1.6,
  };
}

export function dotColor(tone: PillTone | AuditCategory): string {
  const map: Record<string, string> = {
    ok: "var(--success-dot)",
    danger: "var(--danger-500)",
    kill: "var(--danger-500)",
    warn: "#D98A1F",
    neutral: "var(--ink-300)",
    violet: "var(--violet-500)",
    credit: "var(--violet-500)",
    sky: "var(--sky-500)",
    lookup: "var(--sky-500)",
    role: "var(--ink-300)",
    org: "var(--ink-300)",
  };
  return map[tone] || "var(--ink-300)";
}
